/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import {
  flexRender,
  MaterialReactTable,
  MRT_ExpandAllButton,
  MRT_ExpandButton,
  useMaterialReactTable,
} from 'material-react-table';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import styles from './ReportBuildMenuData.module.scss';
import CustomReportHeader from './CustomReportHeader';
import { groupCountsForPdf } from '../../../../components/tables/MTable.utils';
import { Text } from '../../../../components/typography';
import Spacer from '../../../../components/typography/Spacer';
import { useLabels } from '../../../../providers/LabelProvider';
import Icon from '../../../../components/Icon';
import { customColors } from '../../../../utilities';
import { Box, IconButton, Stack } from '@mui/material';
import { actionItemCheck } from '../../../../components/tables/MTableColumns';
import { groupTypes } from '../../../../utilities/reports';
import { useTranslation } from 'react-i18next';
import { useExcelFormatter } from '../../../../components/excel/ExcelExportFormatter';
import { useCurrentUser } from '../../../../providers/UserProvider';
import {
  buildQueryVariables,
  EXTRA_DATA_COLUMN_KEYS,
  excelHeaderLookup,
  computeFlatTablePDF,
  projectRowColumns,
  DATE_COLUMN_KEYS,
} from '@opas-mobile/report-export';
import { getLocalTime } from '../../../../utilities/time';

const queryConfigs = {
  actionItems: {
    doc: loader('./CustomReport.actionItems.graphql'),
    dataKey: 'actionItems',
  },
  inspections: {
    doc: loader('./CustomReport.inspections.graphql'),
    dataKey: 'inspections',
  },
  incidents: {
    doc: loader('./CustomReport.incidents.graphql'),
    dataKey: 'incidents',
  },
  observations: {
    doc: loader('./CustomReport.observations.graphql'),
    dataKey: 'observations',
  },
  audits: {
    doc: loader('./CustomReport.inspections.graphql'),
    dataKey: 'inspections',
  },
  correctiveActions: {
    doc: loader('./CustomReport.correctiveActions.graphql'),
    dataKey: 'correctiveActions',
    queryName: 'correctiveAction',
  },
  timecards: {
    doc: loader('./CustomReport.timecards.graphql'),
    dataKey: 'timecards',
  },
  expenses: {
    doc: loader('./CustomReport.expenses.graphql'),
    dataKey: 'expenses',
  },
};

export default function ReportBuildMenuData({
  model = {},
  workspaces = [],
  options = [],
  readOnly,
  setTablePDF = () => {},
  attachmentId,
  setTableExport = () => {},
}) {
  // eslint-disable-next-line no-unused-vars
  const [tableState, setTableState] = useState({});
  const { models } = useLabels();
  const { t } = useTranslation();
  const { formatRows } = useExcelFormatter();
  const { user } = useCurrentUser();
  const { timeRange } = options || {};
  const { from, to } = timeRange || {};

  const queryKey = model?.value || options?.model || 'actionItems';
  const config = queryConfigs[queryKey] ?? queryConfigs.actionItems;
  const modelFilters = model?.filters || [];

  // A saved column selection (options.columns) means this attachment shows
  // a flat, one-row-per-record table instead of the grouped/counted view —
  // matches the PDF/Excel export's own rule (see report-export's
  // fetchReportPDFData), so the live preview never disagrees with the file.
  const isFlatMode = options?.columns?.length > 0;
  // options.columns is a fresh array reference every parent render even
  // when its content hasn't changed (it comes off menuState, recreated on
  // every edit) — memos/effects below key off this stable primitive
  // instead, or they'd recompute (and, for the effect further down, loop)
  // on every render regardless of whether the selection actually changed.
  const columnsKey = options?.columns?.join(',') ?? '';
  const columns = useMemo(() => {
    const all = model?.columns ?? [];
    if (!isFlatMode) return all;
    // Same pool ReportBuildMenu.jsx's picker offers: model.columns plus the
    // model's id columns (see report-export's EXTRA_DATA_COLUMN_KEYS) — those
    // aren't in model.columns, so built here too, deliberately plain (no
    // custom Cell) to match exactly what the PDF/Excel actually show.
    const idColumns = (EXTRA_DATA_COLUMN_KEYS[queryKey] ?? ['id'])
      .filter((key) => !all.some((c) => (c?.accessorKey ?? c?.id) === key))
      .map((key) => ({
        id: key,
        accessorKey: key,
        header: excelHeaderLookup[key] ?? key,
        // Only dateOfIncident today, but any future date-type extra column
        // needs this too — otherwise it's a raw ISO/offset string on
        // screen instead of the recipient's actual local date and time.
        ...(DATE_COLUMN_KEYS.has(key) && {
          Cell: ({ cell }) => {
            const v = cell.getValue();
            if (!v) return '';
            return getLocalTime(v).format('MMM D, YYYY h:mm A');
          },
        }),
      }));
    const byKey = new Map(
      [...idColumns, ...all].map((c) => [c?.accessorKey ?? c?.id, c]),
    );
    // Column order follows the saved selection order, not model.columns'
    // order — the user controls it via the picker.
    return options.columns.map((key) => byKey.get(key)).filter(Boolean);
  }, [model?.columns, isFlatMode, columnsKey, queryKey]);
  const columnIds = useMemo(
    () => columns.map((c) => c?.accessorKey ?? c?.id),
    [columns],
  );

  const grouping = useMemo(() => {
    if (isFlatMode) return [];
    const ids = [options?.group, options?.subgroup].filter(Boolean).map(String);
    return ids.filter((id) => columnIds.includes(id));
  }, [isFlatMode, options?.group, options?.subgroup, columnIds]);
  // Shared with the package's fetchReportPDFData (PDF/Excel generation) so
  // this live preview can never drift from what actually gets exported —
  // see report-export's buildQueryVariables.
  const variables = useMemo(() => {
    if (!from || !to) return undefined;
    return buildQueryVariables(options, modelFilters) ?? undefined;
  }, [from, to, workspaces, model, modelFilters, options?.filters]);

  const { data, loading, refetch } = useQuery(config.doc, {
    variables: { options: variables ?? undefined },
    skip: !from || !to || !variables || options?.group?.includes('TRIF'),
  });

  const rows = useMemo(
    () => data?.[config.dataKey] ?? [],
    [config.dataKey, data],
  );
  const total = useMemo(
    () => data?.[`${config.queryName || config.dataKey}Count`],
    [data],
  );

  const groupingString = grouping
    .map((g) => t(groupTypes[g]?.title) || g)
    ?.join(', ');

  const table = useMaterialReactTable({
    data: rows,
    columns,
    // In flat mode nothing is grouped, so every row hits the "can't
    // expand" fallback below (actionItemCheck) — an unlabeled green check
    // on every row with no purpose. Disabling both removes the row-expand
    // display column entirely rather than rendering an empty one.
    enableGrouping: !isFlatMode,
    displayColumnDefOptions: {
      'mrt-row-expand': {
        Header: () => {
          const grouping = table.getState().grouping ?? [];
          const expandGroupsOnly = () => {
            if ((grouping?.length ?? 0) <= 1) {
              table.setExpanded({});
              return;
            }
            const maxDepth = grouping.length - 2;
            const expanded = {};
            const walk = (rows) => {
              rows.forEach((r) => {
                if (r.getCanExpand?.()) {
                  if ((r.depth ?? 0) <= maxDepth) {
                    expanded[r.id] = true;
                    if (r.subRows?.length) walk(r.subRows);
                  }
                }
              });
            };
            walk(table.getGroupedRowModel().rows);
            table.setExpanded(expanded);
          };
          return (
            <Stack direction="row" spacing={0} alignItems="center">
              <MRT_ExpandAllButton table={table} />
              {!!options?.subgroup && (
                <IconButton
                  size="small"
                  onClick={expandGroupsOnly}
                  title="Expand groups only"
                >
                  <Icon>unfold_more</Icon>
                </IconButton>
              )}
              <Box>{groupingString}</Box>
            </Stack>
          );
        },
        Cell: ({ row, table }) => {
          if (!row.getCanExpand()) {
            return actionItemCheck({ row });
          }
          const grouping = table.getState().grouping ?? [];
          const level = row.depth ?? 0;
          const colId = grouping[level];
          if (!colId) return null;

          const col = table.getColumn(colId) ?? null;
          const value = row.getValue(colId);
          const renderer = col?.columnDef?.GroupedCell ?? col?.columnDef?.Cell;

          const cell = col && {
            id: `${row.id}_${colId}`,
            row,
            column: col,
            getValue: () => value,
            getContext: () => ({ table, row, column: col }),
          };

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <MRT_ExpandButton row={row} table={table} />
              {renderer && cell ? (
                flexRender(renderer, { table, row, column: col, cell })
              ) : (
                <Box>{String(value ?? '')}</Box>
              )}
            </Stack>
          );
        },
        GroupedCell: ({ row }) => {
          const showLeafIndicator = true;
          return showLeafIndicator ? actionItemCheck({ row }) : null;
        },
        minSize: 200,
        enableResizing: true,
      },
    },
    enableColumnResizing: true,
    groupedColumnMode: 'remove',
    enableExpanding: !isFlatMode,
    enablePagination: true,
    manualPagination: false,
    enableFilters: true,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    enableColumnOrdering: !readOnly,
    enableFilterMatchHighlighting: false,
    enableStickyHeader: true,
    enableStickyFooter: false,
    enableRowVirtualization: true,
    enableColumnActions: false,
    showGlobalFilter: true,
    enableResizing: true,
    enableRowOrdering: false,
    state: {
      isLoading: loading,
      grouping,
    },
    positionToolbarAlertBanner: 'none',
    muiTableBodyRowProps: ({ row }) => ({
      onClick: row.getCanExpand?.()
        ? row.getToggleExpandedHandler()
        : () => model.onClick?.(row),
    }),
    onStateChange: setTableState,
    muiTablePaperProps: {
      style: {
        border: 'none',
        boxShadow: 'none',
        width: '100%',
        maxHeight: 'none',
      },
    },
    muiTableHeadCellProps: { style: { fontSize: '10px', border: 'none' } },
    initialState: {
      density: 'compact',
      expanded: true,
      // MaterialReactTable throws on mount if this references a column not
      // in `columns` — a real crash (not just a no-op), so it can't stay
      // hardcoded now that a column selection can omit dateCreated.
      sorting: columnIds.includes('dateCreated')
        ? [{ id: 'dateCreated', desc: true }]
        : [],
      grouping,
    },
  });

  useEffect(() => {
    let mounted = true;
    if (loading || !attachmentId || !rows?.length) return;
    const cols = table.getVisibleLeafColumns();
    const rm = table.getPrePaginationRowModel();

    if (!cols.length || !rm.rows.length) return;
    // Same flat-vs-grouped rule as the on-screen table/export (see
    // report-export's fetchReportPDFData) — this pushes what the PDF/Excel
    // download buttons actually use (CustomReport.jsx's onPDF/onExcelExport
    // read this state directly, not a fresh fetch), so it has to branch the
    // same way or the downloaded file disagrees with what's on screen.
    const pdfData = isFlatMode
      ? computeFlatTablePDF(rows, options.columns, { getTime: getLocalTime })
      : groupCountsForPdf({
          table,
          group: options.group || 'workspaceId',
          subgroup: options.subgroup || '',
          model: queryKey,
          timeSetting: user?.timeSetting,
        });
    const exportRows = isFlatMode
      ? rows.map((r) => projectRowColumns(r, options.columns))
      : rows;
    const formatted = formatRows(exportRows, {
      model: queryKey,
      rename: {
        workspaceId: 'workspace',
        creatorId: 'creator',
        assigneeId: 'assignee',
        vehicleId: 'vehicle',
      },
      dateOutput: 'date',
    });

    if (!mounted) return;
    setTablePDF((prev) => {
      const prevData = prev[attachmentId];
      if (String(prevData) === String(pdfData)) return prev;
      return { ...prev, [attachmentId]: pdfData };
    });
    setTableExport((prev) => {
      const prevData = prev[attachmentId];
      if (String(prevData) === String(formatted)) return prev;
      return { ...prev, [attachmentId]: formatted };
    });

    return () => {
      mounted = false;
    };
    // options.columns is a fresh array reference every parent render even
    // when its content hasn't changed — depending on it directly here
    // re-fires this effect (and, since setTableExport used to update
    // unconditionally below, re-renders the parent) every render, an
    // infinite loop. columnsKey is a stable primitive for the same content.
  }, [loading, attachmentId, rows, formatRows, isFlatMode, columnsKey]);
  return (
    <div>
      <CustomReportHeader options={options} models={models} total={total} />
      <div className={styles.graphContainer}>
        {!rows?.length ? (
          <div>
            <Text weight="semiBold" textAlign="center">
              No Data Found
            </Text>
            <Spacer />
          </div>
        ) : (
          <div className={styles.table}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <MaterialReactTable table={table} />
            </LocalizationProvider>
          </div>
        )}
        {!!options?.description ? (
          <div>
            <Spacer />
            <Text
              color="secondary"
              weight="semiBold"
              textAlign="center"
              noMargin
            >
              {options.description}
            </Text>
          </div>
        ) : null}
        <div className={styles.flexRight}>
          {total > 100 && (
            <Text noMargin size="sm" color="secondary">
              **Maximum 100 Rows will be displayed. To see full list please
              refer to table
            </Text>
          )}
          {!loading && (
            <Icon
              style={{ fontSize: '1rem', color: customColors.BLUE }}
              onClick={() => refetch()}
              hover
            >
              replay
            </Icon>
          )}
        </div>
      </div>
    </div>
  );
}
